@if (isLoading()) {
  <lib-overlay-loader />
} @else {
  <div
    data-test="department-details-page"
    [class]="floatingPageClasses()"
    [class.flex]="floating()"
    [class.flex-col]="floating()"
    [class.gap-lg]="floating()">
    <!-- Floating: header + department info, users and positions are three cards -->
    <div
      data-test="department-details-info-card"
      [class.floating-card]="floating()"
      [class.p-2xl]="floating()">
      <header
        [class.border-b]="!floating()"
        [class.border-neutral-300]="!floating()"
        [class.py-lg]="!floating()">
        <ng-template #backButton>
          <a
            data-test="department-details-back"
            (click)="goBack()"
            class="bg-white">
            <lib-chevron-down-icon
              [iconClass]="'size-10 text-neutral-900 rotate-90 p-sm rounded-lg cursor-pointer border border-neutral-200'" />
          </a>
        </ng-template>

        @if (!floating()) {
          <div class="flex justify-between items-center mb-2xl">
            <ng-container [ngTemplateOutlet]="backButton" />
          </div>
        }

        <div class="flex justify-between items-center">
          <div class="flex gap-lg items-center">
            @if (floating()) {
              <ng-container [ngTemplateOutlet]="backButton" />
            }
            @if (departmentDetails().image && departmentDetails().image?.url) {
              <img
                [src]="departmentDetails().image?.url ?? ''"
                [alt]="departmentDetails().image?.name ?? ''"
                class="rounded-lg size-16" />
            } @else {
              <div
                class="avatar flex items-center justify-center rounded-lg bg-primary-50">
                <span class="text-2xl rtl:text-xl font-medium text-primary-500">
                  {{ departmentDetails().name | avatar }}
                </span>
              </div>
            }

            <div class="flex flex-col gap-md">
              <div class="flex items-center gap-sm">
                <h2 class="font-semibold text-2xl">
                  {{ departmentDetails().name }}
                </h2>
                <span class="text-neutral-300 font-semibold no-arabic-font"
                  >|</span
                >
                <span class="text-primary-500 font-semibold text-2xl">
                  {{ departmentDetails().identifier }}
                </span>
                @if (departmentDetails().state) {
                  <span
                    class="px-md py-x-sm rounded-2xl text-sm font-medium text-white"
                    [class.bg-success-500]="
                      departmentDetails().state?.value === 'active'
                    "
                    [class.bg-destructive-500]="
                      departmentDetails().state?.value !== 'active'
                    ">
                    {{ departmentDetails().state?.label }}
                  </span>
                }
              </div>

              <p class="text-sm font-medium text-neutral-500">
                #{{ departmentDetails().id }}
              </p>
            </div>
          </div>

          <a
            [routerLink]="[
              `${ADMIN_ROUTES.DEPARTMENTS.FORM}` | tenantPath: 'admin',
              departmentId(),
            ]"
            id="department_details_screen_edit_button"
            class="items-center text-primary-600 font-medium text-base py-sm px-2xl border-none rounded-md flex gap-sm">
            <lib-edit-icon [iconClass]="'size-5 text-primary-600'" />

            {{ 'department_details_screen_edit_button' | transloco }}
          </a>
        </div>
      </header>

      <div class="mt-2xl">
        <div class="view-mode-section-title py-lg mb-2xl">
          <h6 class="">
            {{ 'department_details_screen_title' | transloco }}
          </h6>
        </div>

        <div
          class="mb-4xl grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg">
          <div>
            <p class="view-mode-label">
              {{ 'department_details_screen_legal_entity_label' | transloco }}
            </p>

            <p class="view-mode-value break-word text-neutral-900">
              {{
                departmentDetails().legal_entity
                  ? departmentDetails().legal_entity.name
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>

          <div>
            <p class="view-mode-label">
              {{ 'department_details_screen_parent_label' | transloco }}
            </p>

            <p class="view-mode-value break-word text-neutral-900">
              {{
                departmentDetails().parent
                  ? departmentDetails().parent?.name
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>

          <div>
            <p class="view-mode-label">
              {{
                'department_details_screen_department_manager_label' | transloco
              }}
            </p>

            <p class="view-mode-value break-word text-neutral-900">
              {{
                departmentDetails().direct_manager
                  ? departmentDetails().direct_manager?.name
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>

          <div>
            <p class="view-mode-label">
              {{
                'department_details_screen_department_type_label' | transloco
              }}
            </p>

            <p class="view-mode-value break-word text-neutral-900">
              {{
                departmentDetails().department_type
                  ? departmentDetails().department_type?.label
                  : ('details_not_found_label' | transloco)
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      data-test="department-details-users-card"
      [class.mt-5xl]="!floating()"
      [class.floating-card]="floating()"
      [class.p-2xl]="floating()">
      <div
        class="text-neutral-900 font-semibold text-lg px-lg flex items-center justify-between border-b border-neutral-200">
        <div class="flex items-center gap-lg my-2xl">
          <h6 class="">
            {{
              'department_details_screen_department_users_section_title'
                | transloco
            }}
          </h6>

          <span class="bg-primary-50 rounded-2xl">
            <p class="text-primary-700 px-md py-x-sm font-medium">
              {{ totalItems() }}
            </p>
          </span>
        </div>

        <div>
          <div class="search-input relative flex gap-md items-center">
            <label
              class="flex items-center w-full border border-neutral-300 rounded-md text-sm p-md">
              <input
                type="text"
                [id]="'search'"
                [placeholder]="'search_by_name_placeholder' | transloco"
                [style.cursor]="
                  !departmentUsersCards().length && !searchTerm()
                    ? 'not-allowed'
                    : ''
                "
                [disabled]="!departmentUsersCards().length && !searchTerm()"
                class="bg-transparent text-base w-full pe-2xl placeholder:text-sm placeholder:text-neutral-500"
                [ngModel]="searchTerm()"
                (ngModelChange)="onSearchTermChange($event)" />
              <div
                class="icon absolute end-md inset-y-md cursor-pointer flex items-center">
                <lib-search-icon [iconClass]="'size-5 text-neutral-500'" />
              </div>
            </label>
          </div>
        </div>
      </div>

      @if (isUsersLoading()) {
        <div class="flex flex-col justify-center items-center">
          <lib-loader />
        </div>
      } @else {
        <div class="my-lg">
          <lib-group-user-card
            [list]="departmentUsersCards()"
            [itemType]="'User'"
            [mode]="'view'" />
        </div>
      }

      @if (departmentUsersCards().length) {
        <div class="p-sm">
          <lib-paginator
            class="mt-4xl"
            [sizeOptionsLabel]="
              'pagination_number_of_rows_per_page_label' | transloco
            "
            [nextBtnLabel]="'pagination_next_button_label' | transloco"
            [previousBtnLabel]="'pagination_previous_button_label' | transloco"
            [currentPage]="currentPage()"
            [totalPages]="totalPages()"
            [totalItems]="totalItems()"
            [pageItemCount]="departmentUsersCards().length"
            (goTo)="handlePagination($event)"
            (next)="handlePagination($event)"
            (previous)="handlePagination($event)"></lib-paginator>
        </div>
      }
    </div>

    @if (isDepartmentsUpdateEnabled()) {
      <section
        id="positions"
        data-test="department-details-positions-card"
        [class.mt-5xl]="!floating()"
        [class.floating-card]="floating()"
        [class.p-2xl]="floating()"
        data-testid="department-details-positions">
        <div
          class="text-neutral-900 font-semibold text-lg px-lg flex items-center gap-lg border-b border-neutral-200 py-2xl">
          <h6>
            {{
              'department_details_screen_positions_section_title' | transloco
            }}
          </h6>
          <span class="bg-primary-50 rounded-2xl">
            <p class="text-primary-700 px-md py-x-sm font-medium">
              {{ positionsTotal() }}
            </p>
          </span>
        </div>

        @if (isPositionsLoading()) {
          <div class="flex justify-center my-lg">
            <lib-loader />
          </div>
        } @else if (hasPositionsError()) {
          <div
            class="flex items-center justify-between gap-md my-lg p-md rounded-md bg-destructive-50 text-destructive-700 text-sm"
            role="alert"
            data-testid="department-details-positions-error">
            <span>{{ 'department_quick_view_section_error' | transloco }}</span>
            <button
              type="button"
              class="font-medium underline cursor-pointer"
              (click)="getDepartmentPositions()">
              {{ 'department_retry_button' | transloco }}
            </button>
          </div>
        } @else {
          <div
            class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-md my-lg">
            @for (position of positions(); track position.id) {
              <lib-department-position-card [position]="position" />
            } @empty {
              <p class="text-sm text-neutral-500">
                {{ 'department_quick_view_no_positions' | transloco }}
              </p>
            }
          </div>
          @if (positionsTotalPages() > 1) {
            <div class="p-sm">
              <lib-paginator
                [nextBtnLabel]="'pagination_next_button_label' | transloco"
                [previousBtnLabel]="
                  'pagination_previous_button_label' | transloco
                "
                [currentPage]="positionsPage()"
                [totalPages]="positionsTotalPages()"
                [totalItems]="positionsTotal()"
                [pageItemCount]="positions().length"
                (goTo)="handlePositionsPagination($event)"
                (next)="handlePositionsPagination($event)"
                (previous)="handlePositionsPagination($event)"></lib-paginator>
            </div>
          }
        }
      </section>
    }
  </div>
}
